import React from 'react';
import { TagChip } from '../../../shared/ui/components/TagChip';
import { SimpleTagChip } from '../../../shared/ui/components/SimpleTagChip';
import { useSearch } from '../../../shared/contexts/SearchContext';

interface ActiveFilter {
  type: 'tag' | 'app' | 'search';
  id?: string;
  name: string;
  negative?: boolean;
}

interface NavigationHeaderProps {
  onBackToSearch: () => void;
  currentIndex: number;
  totalReferences: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  activeFilters?: ActiveFilter[];
  onRemoveFilter?: (type: 'tag' | 'app' | 'search', id?: string) => void;
}

export function NavigationHeader({
  onBackToSearch,
  currentIndex,
  totalReferences,
  hasPrevious,
  hasNext,
  onNavigatePrevious,
  onNavigateNext,
  activeFilters = [],
  onRemoveFilter,
}: NavigationHeaderProps) {
  const { filterState, filterDispatch } = useSearch();
  
  // Check if new filtering system has active filters
  const hasNewFilters = Object.keys(filterState.tags).length > 0 || (filterState.searchTerm && filterState.searchTerm.trim().length > 0);

  return (
    <div className="w-full bg-white sticky top-0 z-40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full flex items-center justify-between h-16">
          {/* Back Button */}
          <button
            onClick={onBackToSearch}
            className="px-4 py-2 bg-yellow-300 text-black font-bold uppercase rounded hover:bg-yellow-400 transition-colors"
          >
            <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Search
          </button>

          {/* Position Indicator */}
          {totalReferences > 0 && (
            <div className="px-4 py-2 bg-blue-300 text-black font-bold uppercase rounded text-sm">
              {currentIndex + 1} of {totalReferences}
            </div>
          )}

          {/* Navigation Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onNavigatePrevious}
              disabled={!hasPrevious}
              className="p-2 bg-green-300 text-black font-bold rounded hover:bg-green-400 transition-colors disabled:bg-gray-200 disabled:text-gray-500"
              title="Previous reference (←)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={onNavigateNext}
              disabled={!hasNext}
              className="p-2 bg-green-300 text-black font-bold rounded hover:bg-green-400 transition-colors disabled:bg-gray-200 disabled:text-gray-500"
              title="Next reference (→)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Active Filters Display */}
        {(activeFilters.length > 0 || hasNewFilters) && (
          <div className="pb-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="font-bold uppercase">🎯 Filtered by:</span>
              <div className="flex flex-wrap gap-1">
                {/* New Filter System Tags */}
                {hasNewFilters && Object.entries(filterState.tags).map(([tagId, { value, mode }]) => (
                  <SimpleTagChip
                    key={tagId}
                    tag={value}
                    mode={mode}
                    onClick={() => {
                      filterDispatch({
                        type: 'TOGGLE_TAG_MODE',
                        tagId,
                        value,
                        category: undefined
                      });
                    }}
                    size="sm"
                  />
                ))}
                
                {/* New Filter System Search Term */}
                {hasNewFilters && filterState.searchTerm && filterState.searchTerm.trim() && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>"{filterState.searchTerm}"</span>
                    <button
                      type="button"
                      onClick={() => filterDispatch({ type: 'SET_SEARCH_TERM', term: '' })}
                      className="ml-1 text-gray-500 hover:text-gray-700"
                    >
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                
                {/* Legacy Filter System */}
                {!hasNewFilters && activeFilters.map((filter, index) => {
                  if (filter.type === 'tag') {
                    return (
                      <div key={`tag-${filter.id || filter.name}-${index}`} className="inline-flex items-center">
                        <TagChip
                          tag={{ _id: filter.id as any, name: filter.name }}
                          variant={filter.negative ? 'negative' : 'filter'}
                          size="sm"
                          onClick={onRemoveFilter ? () => onRemoveFilter('tag', filter.id) : undefined}
                        />
                      </div>
                    );
                  } else if (filter.type === 'app') {
                    return (
                      <div
                        key={`app-${filter.id || filter.name}-${index}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          filter.negative
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        <span className="font-mono text-xs">
                          {filter.negative ? '-app:' : 'app:'}
                        </span>
                        <span>{filter.name}</span>
                        {onRemoveFilter && (
                          <button
                            type="button"
                            onClick={() => onRemoveFilter('app', filter.id)}
                            className="ml-1 text-current opacity-70 hover:opacity-100"
                          >
                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  } else if (filter.type === 'search') {
                    return (
                      <div
                        key={`search-${index}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <span>"{filter.name}"</span>
                        {onRemoveFilter && (
                          <button
                            type="button"
                            onClick={() => onRemoveFilter('search')}
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
